if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.initProductMediaViewToggle();
      }

      disconnectedCallback() {
        this.productMediaViewVariantUnsubscriber?.();
        if (this.productMediaViewLayoutHandler) {
          window.removeEventListener('resize', this.productMediaViewLayoutHandler);
          window.removeEventListener('scroll', this.productMediaViewLayoutHandler, true);
        }
      }

      getMediaElement(mediaId) {
        if (!mediaId) return null;

        let cleanMediaId = String(mediaId);
        if (cleanMediaId.indexOf('-') !== -1) {
          const parts = cleanMediaId.split('-');
          cleanMediaId = parts[parts.length - 1];
        }

        const candidates = Array.from(
          this.querySelectorAll(`[data-media-id="${cleanMediaId}"], [data-media-id="${mediaId}"]`)
        );

        return candidates.find((element) => this.isElementVisible(element)) || candidates[0] || null;
      }

      initProductMediaViewToggle() {
        if (this.productMediaViewInitialized) return;

        this.productMediaViewDataScript = this.querySelector('[data-product-media-view-data]');
        this.productMediaViewControls = this.querySelector('[data-product-media-view-controls]');
        this.productMediaViewToggle = this.querySelector('[data-product-media-view-toggle]');
        this.productMediaViewOpenButton = this.querySelector('[data-product-media-view-button="open"]');
        this.productMediaViewPrivacyButton = this.querySelector('[data-product-media-view-button="privacy"]');
        this.productMediaRoomSelector = this.querySelector('[data-product-media-room-selector]');
        this.productMediaRoomButtons = Array.from(this.querySelectorAll('[data-product-media-room-button]'));
        this.productMediaViewPrivacyOverlay = this.querySelector('[data-product-media-privacy-overlay]');
        this.productMediaViewPrivacyImage = this.querySelector('[data-product-media-privacy-image]');

        if (
          !this.productMediaViewDataScript ||
          !this.productMediaViewControls ||
          !this.productMediaViewToggle ||
          !this.productMediaViewOpenButton ||
          !this.productMediaViewPrivacyButton ||
          !this.productMediaViewPrivacyOverlay ||
          !this.productMediaViewPrivacyImage
        ) {
          return;
        }

        try {
          this.productMediaViewData = JSON.parse(this.productMediaViewDataScript.textContent || '{}');
        } catch (e) {
          this.productMediaViewData = null;
        }

        if (!this.productMediaViewData?.variants) return;

        this.productMediaViewInitialized = true;
        const persistedState = this.getProductMediaViewState();
        this.productMediaViewMode = persistedState?.mode || 'open';
        this.productMediaViewRoom = persistedState?.room || this.productMediaViewData.initialRoom || 'living';
        this.productMediaViewVariantId = this.getCurrentVariantId() || this.productMediaViewData.initialVariantId;
        this.productMediaViewHasRoomSelector = this.productMediaRoomButtons.length > 0;
        this.productMediaViewHasAnyPrivacy = Object.values(this.productMediaViewData.variants).some(
          (variantData) =>
            ['living', 'bedroom', 'office'].some((roomKey) =>
              this.roomDataHasPrivacy(this.getRoomViewData(variantData, roomKey))
            )
        );

        this.productMediaViewOpenButton.addEventListener('click', () => this.showOpenVariantView());
        this.productMediaViewPrivacyButton.addEventListener('click', () => this.showPrivacyVariantView());
        this.productMediaRoomButtons.forEach((button) => {
          button.addEventListener('click', () => this.setProductMediaRoom(button.dataset.productMediaRoomButton));
        });

        this.productMediaViewLayoutHandler = () => {
          if (this.productMediaViewMode === 'privacy') this.placePrivacyOverlay();
        };
        window.addEventListener('resize', this.productMediaViewLayoutHandler);
        window.addEventListener('scroll', this.productMediaViewLayoutHandler, true);

        if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.productMediaViewVariantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data?.sectionId !== this.dataset.section) return;
            this.updateVariantViewData(data?.variant?.id);
          });
        }

        this.updateVariantViewData(this.productMediaViewVariantId);
      }

      getCurrentVariantId() {
        const selectedVariantScript = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');
        if (!selectedVariantScript) return null;

        try {
          return JSON.parse(selectedVariantScript.textContent || '{}')?.id || null;
        } catch (e) {
          return null;
        }
      }

      getCurrentVariantViewData() {
        if (!this.productMediaViewVariantId) return null;
        return this.productMediaViewData?.variants?.[String(this.productMediaViewVariantId)] || null;
      }

      getProductMediaViewState() {
        const sectionId = this.dataset.section || this.dataset.sectionId;
        return window.__productMediaViewState?.[sectionId] || null;
      }

      saveProductMediaViewState() {
        const sectionId = this.dataset.section || this.dataset.sectionId;
        if (!sectionId) return;

        window.__productMediaViewState = window.__productMediaViewState || {};
        window.__productMediaViewState[sectionId] = {
          mode: this.productMediaViewMode || 'open',
          room: this.productMediaViewRoom || 'living',
        };
      }

      normalizeImageData(imageData) {
        if (!imageData) return null;

        if (typeof imageData === 'string') {
          return {
            src: imageData,
            thumbSrc: imageData,
            zoomSrc: imageData,
          };
        }

        if (typeof imageData === 'object' && imageData.src) {
          return imageData;
        }

        return null;
      }

      normalizeRoomData(roomData = {}) {
        return {
          ...roomData,
          openImage: this.normalizeImageData(roomData.openImage),
          galleryImages: Array.isArray(roomData.galleryImages)
            ? roomData.galleryImages.map((image) => this.normalizeImageData(image)).filter(Boolean)
            : [],
        };
      }

      getVariantColorValue(variantData) {
        const rawColorOptionIndex = this.productMediaViewData?.colorOptionIndex;
        if (rawColorOptionIndex === null || rawColorOptionIndex === undefined || rawColorOptionIndex === '') return null;

        const colorOptionIndex = Number(rawColorOptionIndex);
        if (!Number.isInteger(colorOptionIndex) || colorOptionIndex < 0) return null;

        return Array.isArray(variantData?.options) ? variantData.options[colorOptionIndex] || null : null;
      }

      getSameColorRoomFallback(variantData, roomKey = 'living') {
        const selectedColor = this.getVariantColorValue(variantData);
        if (!selectedColor || !this.productMediaViewData?.variants) return null;

        const fallbackRoomData = {};

        Object.values(this.productMediaViewData.variants).forEach((candidateVariantData) => {
          if (candidateVariantData === variantData) return;
          if (this.getVariantColorValue(candidateVariantData) !== selectedColor) return;

          const candidateRoomData = this.normalizeRoomData(candidateVariantData?.rooms?.[roomKey] || {});
          if (!this.roomDataHasAnyImage(candidateRoomData)) return;

          fallbackRoomData.label = fallbackRoomData.label || candidateRoomData.label;
          fallbackRoomData.openMediaId = fallbackRoomData.openMediaId || candidateRoomData.openMediaId;
          fallbackRoomData.openImage = fallbackRoomData.openImage || candidateRoomData.openImage;
          fallbackRoomData.privacyImage = fallbackRoomData.privacyImage || candidateRoomData.privacyImage;
          fallbackRoomData.privacyImageWidth = fallbackRoomData.privacyImageWidth || candidateRoomData.privacyImageWidth;
          fallbackRoomData.privacyImageHeight = fallbackRoomData.privacyImageHeight || candidateRoomData.privacyImageHeight;
          fallbackRoomData.galleryImages = fallbackRoomData.galleryImages?.length
            ? fallbackRoomData.galleryImages
            : candidateRoomData.galleryImages;
          fallbackRoomData.hasCustomGallery = !!(
            fallbackRoomData.hasCustomGallery ||
            candidateRoomData.hasCustomGallery ||
            candidateRoomData.openImage ||
            candidateRoomData.galleryImages?.length
          );
        });

        return this.roomDataHasAnyImage(fallbackRoomData) ? fallbackRoomData : null;
      }

      mergeRoomData(primaryRoomData = {}, fallbackRoomData = {}) {
        const primaryGalleryImages = Array.isArray(primaryRoomData.galleryImages) ? primaryRoomData.galleryImages : [];
        const fallbackGalleryImages = Array.isArray(fallbackRoomData.galleryImages) ? fallbackRoomData.galleryImages : [];
        const galleryImages = primaryGalleryImages.length ? primaryGalleryImages : fallbackGalleryImages;

        return {
          ...fallbackRoomData,
          ...primaryRoomData,
          label: primaryRoomData.label || fallbackRoomData.label,
          openMediaId: primaryRoomData.openMediaId || fallbackRoomData.openMediaId,
          openImage: primaryRoomData.openImage || fallbackRoomData.openImage,
          privacyImage: primaryRoomData.privacyImage || fallbackRoomData.privacyImage,
          privacyImageWidth: primaryRoomData.privacyImageWidth || fallbackRoomData.privacyImageWidth,
          privacyImageHeight: primaryRoomData.privacyImageHeight || fallbackRoomData.privacyImageHeight,
          galleryImages,
          hasCustomGallery: !!(
            primaryRoomData.hasCustomGallery ||
            fallbackRoomData.hasCustomGallery ||
            primaryRoomData.openImage ||
            fallbackRoomData.openImage ||
            galleryImages.length
          ),
        };
      }

      getRoomViewData(variantData, roomKey = 'living') {
        if (!variantData) return null;

        const fallbackLivingRoom = this.normalizeRoomData({
          label: 'Living Room',
          openMediaId: variantData.openMediaId,
          openImage: variantData.openImage,
          privacyImage: variantData.privacyImage,
          privacyImageWidth: variantData.privacyImageWidth,
          privacyImageHeight: variantData.privacyImageHeight,
          galleryImages: [],
          hasCustomGallery: false,
        });
        const rooms = variantData.rooms || {};
        const livingRoom = this.normalizeRoomData(rooms.living || fallbackLivingRoom);
        const requestedRoom = this.normalizeRoomData(rooms[roomKey] || {});
        const sameColorRoomFallback = this.normalizeRoomData(this.getSameColorRoomFallback(variantData, roomKey) || {});
        const requestedRoomWithFallback = this.mergeRoomData(requestedRoom, sameColorRoomFallback);
        const requestedRoomHasImages = roomKey === 'living' || this.roomDataHasAnyImage(requestedRoomWithFallback);
        const roomSource = requestedRoomHasImages ? requestedRoomWithFallback : {};
        const usesCustomGallery = !!(
          roomSource.hasCustomGallery ||
          (roomKey === 'living' && livingRoom.hasCustomGallery)
        );

        return {
          ...livingRoom,
          ...roomSource,
          room: roomKey,
          label: roomSource.label || livingRoom.label || roomKey,
          openMediaId: roomSource.openMediaId || livingRoom.openMediaId,
          openImage: roomSource.openImage || livingRoom.openImage,
          privacyImage: roomSource.privacyImage || livingRoom.privacyImage,
          privacyImageWidth: roomSource.privacyImageWidth || livingRoom.privacyImageWidth,
          privacyImageHeight: roomSource.privacyImageHeight || livingRoom.privacyImageHeight,
          galleryImages: usesCustomGallery
            ? roomSource.galleryImages || []
            : livingRoom.galleryImages || [],
          hasCustomGallery: usesCustomGallery,
          hasRequestedRoomImages: requestedRoomHasImages,
        };
      }

      getCurrentRoomViewData() {
        return this.getRoomViewData(this.getCurrentVariantViewData(), this.productMediaViewRoom || 'living');
      }

      roomDataHasAnyImage(roomData) {
        return !!(
          roomData?.openImage ||
          roomData?.privacyImage ||
          roomData?.hasCustomGallery ||
          (Array.isArray(roomData?.galleryImages) && roomData.galleryImages.length)
        );
      }

      roomDataHasPrivacy(roomData) {
        return !!roomData?.privacyImage;
      }

      updateVariantViewData(variantId, options = {}) {
        if (!this.productMediaViewInitialized) return;
        if (variantId) this.productMediaViewVariantId = String(variantId);

        const roomData = this.getCurrentRoomViewData();
        const hasPrivacyImage = this.roomDataHasPrivacy(roomData);

        this.productMediaViewControls.hidden = !(this.productMediaViewHasAnyPrivacy || this.productMediaViewHasRoomSelector);
        this.productMediaViewToggle.hidden = !this.productMediaViewHasAnyPrivacy && !hasPrivacyImage;
        if (this.productMediaRoomSelector) {
          this.productMediaRoomSelector.hidden = !this.productMediaViewHasRoomSelector;
        }
        this.productMediaViewPrivacyButton.disabled = !hasPrivacyImage;
        this.productMediaViewPrivacyButton.setAttribute('aria-disabled', String(!hasPrivacyImage));

        if (!hasPrivacyImage && this.productMediaViewMode === 'privacy') {
          this.productMediaViewMode = 'open';
          this.saveProductMediaViewState();
          this.showOpenVariantView({ transitionMedia: false });
          return;
        }

        if (this.productMediaViewMode === 'privacy' && options.keepPrivacy !== false) {
          this.showPrivacyVariantView({ transitionMedia: false });
        } else {
          this.showOpenVariantView({ transitionMedia: false });
        }
      }

      updateProductMediaViewButtons() {
        if (!this.productMediaViewOpenButton || !this.productMediaViewPrivacyButton) return;

        const roomData = this.getCurrentRoomViewData();
        const hasPrivacyImage = this.roomDataHasPrivacy(roomData);

        this.productMediaViewPrivacyButton.disabled = !hasPrivacyImage;
        this.productMediaViewPrivacyButton.setAttribute('aria-disabled', String(!hasPrivacyImage));
        this.productMediaViewOpenButton.setAttribute('aria-pressed', String(this.productMediaViewMode === 'open'));
        this.productMediaViewPrivacyButton.setAttribute('aria-pressed', String(this.productMediaViewMode === 'privacy'));

        this.productMediaRoomButtons?.forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.productMediaRoomButton === this.productMediaViewRoom));
        });
      }

      async showOpenVariantView(options = {}) {
        if (!this.productMediaViewInitialized) return;

        this.productMediaViewMode = 'open';
        this.saveProductMediaViewState();
        this.updateProductMediaViewButtons();
        this.hidePrivacyOverlay();
        this.fadeOutVariantOverlay(this.productMediaViewHeldOverlay);
        this.productMediaViewHeldOverlay = null;

        const roomData = this.getCurrentRoomViewData();
        const shouldTransitionMedia = options.transitionMedia !== false;
        const usesRoomGallery = await this.applyCurrentRoomGallery(roomData, { transitionMedia: shouldTransitionMedia });

        if (!usesRoomGallery && roomData?.openImage) {
          const appliedDirectImage = await this.applyDirectRoomImage(roomData.openImage, {
            transitionMedia: shouldTransitionMedia,
          });
          if (appliedDirectImage) return;
        }

        if (!usesRoomGallery && shouldTransitionMedia && roomData?.openMediaId) {
          await this.transitionToMedia(`${this.dataset.section}-${roomData.openMediaId}`);
        }
      }

      async showPrivacyVariantView(options = {}) {
        if (!this.productMediaViewInitialized) return;

        const roomData = this.getCurrentRoomViewData();
        if (!roomData?.privacyImage) {
          this.productMediaViewMode = 'open';
          this.saveProductMediaViewState();
          this.updateProductMediaViewButtons();
          await this.showOpenVariantView(options);
          return;
        }

        this.productMediaViewMode = 'privacy';
        this.saveProductMediaViewState();
        this.updateProductMediaViewButtons();

        const shouldTransitionMedia = options.transitionMedia !== false;
        const usesRoomGallery = await this.applyCurrentRoomGallery(roomData, { transitionMedia: shouldTransitionMedia });
        let appliedDirectImage = false;

        if (!usesRoomGallery && roomData?.openImage) {
          appliedDirectImage = await this.applyDirectRoomImage(roomData.openImage, {
            transitionMedia: shouldTransitionMedia,
          });
        }

        if (!usesRoomGallery && !appliedDirectImage && shouldTransitionMedia && roomData.openMediaId) {
          await this.transitionToMedia(`${this.dataset.section}-${roomData.openMediaId}`);
        }

        await this.preloadPrivacyImage(roomData.privacyImage);
        if (roomData.privacyImageWidth && roomData.privacyImageHeight) {
          this.productMediaViewPrivacyImage.width = roomData.privacyImageWidth;
          this.productMediaViewPrivacyImage.height = roomData.privacyImageHeight;
        }
        this.productMediaViewPrivacyImage.src = roomData.privacyImage;
        this.placePrivacyOverlay();
        this.productMediaViewPrivacyOverlay.hidden = false;

        requestAnimationFrame(() => {
          this.productMediaViewPrivacyOverlay.classList.add('is-visible');
          this.fadeOutVariantOverlay(this.productMediaViewHeldOverlay);
          this.productMediaViewHeldOverlay = null;
        });
      }

      setProductMediaRoom(roomKey, options = {}) {
        if (!this.productMediaViewInitialized || !roomKey) return;

        this.productMediaViewRoom = roomKey;
        this.saveProductMediaViewState();
        this.updateProductMediaViewButtons();

        if (this.productMediaViewMode === 'privacy') {
          this.showPrivacyVariantView(options);
        } else {
          this.showOpenVariantView(options);
        }
      }

      hidePrivacyOverlay() {
        if (!this.productMediaViewPrivacyOverlay) return;

        this.productMediaViewPrivacyOverlay.classList.remove('is-visible');
        setTimeout(() => {
          if (this.productMediaViewMode !== 'privacy') {
            this.productMediaViewPrivacyOverlay.hidden = true;
          }
        }, 280);
      }

      placePrivacyOverlay() {
        if (!this.productMediaViewPrivacyOverlay) return false;

        const activeMedia = this.getActiveMediaElement();
        const overlayHost = activeMedia?.closest('.product-media-container, .media-gallery__grid-item, .swiper-slide');
        if (!overlayHost) return false;

        if (window.getComputedStyle(overlayHost).position === 'static') {
          overlayHost.style.position = 'relative';
        }

        if (this.productMediaViewPrivacyOverlay.parentElement !== overlayHost) {
          overlayHost.appendChild(this.productMediaViewPrivacyOverlay);
        }

        return true;
      }

      preloadPrivacyImage(imageUrl) {
        if (!imageUrl) return Promise.resolve();

        return new Promise((resolve) => {
          const image = new Image();
          let complete = false;
          const finish = () => {
            if (complete) return;
            complete = true;
            resolve();
          };

          image.decoding = 'async';
          image.addEventListener('load', () => {
            if (typeof image.decode === 'function') {
              image.decode().then(finish).catch(finish);
            } else {
              finish();
            }
          }, { once: true });
          image.addEventListener('error', finish, { once: true });
          image.src = imageUrl;

          setTimeout(finish, 6000);
        });
      }

      getActiveMediaElement() {
        const candidates = Array.from(
          this.querySelectorAll(
            '[data-media-id].active, .swiper-slide-active [data-media-id], .media-gallery__grid [data-media-id], [data-media-id]'
          )
        );

        return candidates.find((element) => this.isElementVisible(element)) || candidates[0] || null;
      }

      isElementVisible(element) {
        if (!element) return false;

        const rect = element.getBoundingClientRect();
        if (!rect.width || !rect.height) return false;

        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }

      async transitionToMedia(mediaId) {
        const targetMedia = this.getMediaElement(mediaId);
        if (!targetMedia) return false;

        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        const overlay = reduceMotion ? null : this.createVariantFadeOverlay();
        const shouldHoldPrivacyOverlay = this.productMediaViewMode === 'privacy' && overlay;

        await this.waitForImages(targetMedia);

        const didActivate = this.setActiveMedia(mediaId, false);
        if (shouldHoldPrivacyOverlay) {
          this.productMediaViewHeldOverlay = overlay;
        } else if (overlay) {
          this.fadeOutVariantOverlay(overlay);
        }

        return didActivate;
      }

      createVariantFadeOverlay() {
        const activeMedia = this.getActiveMediaElement();
        const visiblePrivacyOverlay =
          this.productMediaViewPrivacyOverlay &&
          !this.productMediaViewPrivacyOverlay.hidden &&
          this.productMediaViewPrivacyOverlay.classList.contains('is-visible');
        const privacyImage = visiblePrivacyOverlay ? this.productMediaViewPrivacyImage : null;
        const image = privacyImage || activeMedia?.querySelector('img');
        const imageSource = image?.currentSrc || image?.src;

        if (!activeMedia || !image || !imageSource) return null;

        const rect = (visiblePrivacyOverlay ? this.productMediaViewPrivacyOverlay : activeMedia).getBoundingClientRect();
        if (!rect.width || !rect.height) return null;

        const imageStyle = window.getComputedStyle(image);
        const overlay = document.createElement('div');

        overlay.className = 'media-gallery__variant-fade-overlay';
        overlay.style.position = 'fixed';
        overlay.style.left = `${rect.left}px`;
        overlay.style.top = `${rect.top}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
        overlay.style.backgroundImage = `url("${imageSource.replace(/"/g, '\\"')}")`;
        overlay.style.backgroundSize = imageStyle.objectFit || 'contain';
        overlay.style.backgroundPosition = imageStyle.objectPosition || 'center center';
        overlay.style.backgroundRepeat = 'no-repeat';
        overlay.style.pointerEvents = 'none';
        overlay.style.zIndex = '2147483000';
        overlay.style.opacity = '1';
        overlay.style.transition = 'opacity 420ms ease';
        overlay.style.willChange = 'opacity';

        document.body.appendChild(overlay);

        return overlay;
      }

      fadeOutVariantOverlay(overlay) {
        if (!overlay) return;

        requestAnimationFrame(() => {
          overlay.style.opacity = '0';
          setTimeout(() => overlay.remove(), 460);
        });
      }

      setActiveMedia(mediaId, smooth = false) {
        if (!mediaId) return false;

        const element = this.getMediaElement(mediaId);
        if (!element) return false;

        this.querySelectorAll('[data-media-id].active').forEach((node) => node.classList.remove('active'));

        element.classList.add('active');

        const allMediaElements = Array.from(this.querySelectorAll('[data-media-id]'));
        const mediaIndex = allMediaElements.indexOf(element);

        this.syncSlideshow(element, mediaIndex, smooth);
        this.syncThumbnails(element, mediaIndex);

        if (this.classList.contains('media-gallery--grid')) {
          this.swapGridMedia(element, mediaIndex);
        }

        return true;
      }

      getRoomGalleryImages(roomData) {
        if (!roomData?.hasCustomGallery) return [];

        const images = [];
        const openImage = this.normalizeImageData(roomData.openImage);
        if (openImage) images.push(openImage);

        if (Array.isArray(roomData.galleryImages)) {
          roomData.galleryImages.forEach((image) => {
            const normalizedImage = this.normalizeImageData(image);
            if (normalizedImage) images.push(normalizedImage);
          });
        }

        return images;
      }

      async applyCurrentRoomGallery(roomData, options = {}) {
        const roomImages = this.getRoomGalleryImages(roomData);

        if (!roomImages.length) {
          await this.restoreOriginalRoomGallery(options);
          return false;
        }

        return this.rebuildRoomGallery(roomImages, options);
      }

      async applyDirectRoomImage(imageData, options = {}) {
        const image = this.normalizeImageData(imageData);
        if (!image?.src) return false;

        this.ensureRoomGallerySnapshot();

        const overlay = options.transitionMedia !== false ? this.createVariantFadeOverlay() : null;
        const activeMedia = this.getActiveMediaElement() || this.querySelector('.product-media-container, .media-gallery__grid-item, .swiper-slide');
        if (!activeMedia) {
          this.fadeOutVariantOverlay(overlay);
          return false;
        }

        this.applyRoomImageToNode(activeMedia, image, 'main');
        this.applyDirectThumbnailImage(image);
        this.applyDirectZoomImage(image);
        this.productMediaRoomGalleryMutated = true;

        await this.waitForImages(activeMedia);
        this.fadeOutVariantOverlay(overlay);

        return true;
      }

      applyDirectThumbnailImage(imageData) {
        const image = this.normalizeImageData(imageData);
        if (!image) return;

        const thumbnail =
          this.querySelector('.swiper-controls__thumbnail.swiper-slide-thumb-active img') ||
          this.querySelector('.swiper-controls__thumbnail[data-index="0"] img') ||
          this.querySelector('.swiper-controls__thumbnails-container img');

        if (thumbnail) this.applyRoomImageToElement(thumbnail, image, true);
      }

      applyDirectZoomImage(imageData) {
        const image = this.normalizeImageData(imageData);
        if (!image) return;

        const { thumbnails, mediaList } = this.getZoomDialogParts();
        const zoomImage = mediaList?.querySelector('.product-media-container img, img');
        const zoomThumbnail = thumbnails?.querySelector('[aria-selected="true"] img, img');

        if (zoomImage) this.applyRoomImageToElement(zoomImage, image, false);
        if (zoomThumbnail) this.applyRoomImageToElement(zoomThumbnail, image, true);

        this.refreshZoomDialogCache();
      }

      getMainSlidesWrapper() {
        return this.querySelector('swiper-component .swiper-main .swiper-wrapper');
      }

      getThumbnailSlidesWrapper() {
        return this.querySelector('.swiper-controls__thumbnails-container .swiper-wrapper');
      }

      getGridWrapper() {
        return this.querySelector('.media-gallery__grid');
      }

      getZoomDialogParts() {
        const zoomDialog = this.querySelector('zoom-dialog');

        return {
          zoomDialog,
          thumbnails: zoomDialog?.querySelector('.dialog-thumbnails-list') || null,
          mediaList: zoomDialog?.querySelector('.dialog-zoomed-gallery') || null,
        };
      }

      cloneElementChildren(wrapper) {
        if (!wrapper) return [];

        return Array.from(wrapper.children)
          .filter((node) => !node.classList.contains('swiper-slide-duplicate'))
          .map((node) => node.cloneNode(true));
      }

      ensureRoomGallerySnapshot() {
        if (this.productMediaRoomSnapshot) return;

        const { thumbnails, mediaList } = this.getZoomDialogParts();

        this.productMediaRoomSnapshot = {
          mainSlides: this.cloneElementChildren(this.getMainSlidesWrapper()),
          thumbnails: this.cloneElementChildren(this.getThumbnailSlidesWrapper()),
          gridItems: this.cloneElementChildren(this.getGridWrapper()),
          zoomThumbnails: this.cloneElementChildren(thumbnails),
          zoomItems: this.cloneElementChildren(mediaList),
        };
      }

      restoreChildrenFromSnapshot(wrapper, nodes) {
        if (!wrapper || !nodes?.length) return false;

        wrapper.innerHTML = '';
        nodes.forEach((node) => wrapper.appendChild(node.cloneNode(true)));

        return true;
      }

      async restoreOriginalRoomGallery(options = {}) {
        if (!this.productMediaRoomGalleryMutated || !this.productMediaRoomSnapshot) return false;

        const overlay = options.transitionMedia !== false ? this.createVariantFadeOverlay() : null;
        const { thumbnails, mediaList } = this.getZoomDialogParts();

        this.destroyRoomGallerySwipers();
        this.restoreChildrenFromSnapshot(this.getMainSlidesWrapper(), this.productMediaRoomSnapshot.mainSlides);
        this.restoreChildrenFromSnapshot(this.getThumbnailSlidesWrapper(), this.productMediaRoomSnapshot.thumbnails);
        this.restoreChildrenFromSnapshot(this.getGridWrapper(), this.productMediaRoomSnapshot.gridItems);
        this.restoreChildrenFromSnapshot(thumbnails, this.productMediaRoomSnapshot.zoomThumbnails);
        this.restoreChildrenFromSnapshot(mediaList, this.productMediaRoomSnapshot.zoomItems);

        this.productMediaRoomGalleryMutated = false;
        this.refreshZoomDialogCache();
        this.initializeRoomGallerySwipers();
        await this.waitForImages(this);
        this.fadeOutVariantOverlay(overlay);

        return true;
      }

      async rebuildRoomGallery(images, options = {}) {
        if (!images.length) return false;

        this.ensureRoomGallerySnapshot();

        const overlay = options.transitionMedia !== false ? this.createVariantFadeOverlay() : null;
        const sectionKey = String(this.dataset.section || this.dataset.sectionId || 'section').replace(/[^A-Za-z0-9_]/g, '_');
        const roomKey = String(this.productMediaViewRoom || 'room').replace(/[^A-Za-z0-9_]/g, '_');
        const mediaIds = images.map((_, index) => `room_${sectionKey}_${roomKey}_${index}`);
        const { thumbnails, mediaList } = this.getZoomDialogParts();

        this.destroyRoomGallerySwipers();

        const updatedMain = this.replaceRoomGalleryNodes(
          this.getMainSlidesWrapper(),
          this.productMediaRoomSnapshot.mainSlides,
          images,
          mediaIds,
          'main'
        );
        const updatedThumbnails = this.replaceRoomGalleryNodes(
          this.getThumbnailSlidesWrapper(),
          this.productMediaRoomSnapshot.thumbnails,
          images,
          mediaIds,
          'thumbnail'
        );
        const updatedGrid = this.replaceRoomGalleryNodes(
          this.getGridWrapper(),
          this.productMediaRoomSnapshot.gridItems,
          images,
          mediaIds,
          'grid'
        );
        const updatedZoomThumbnails = this.replaceRoomGalleryNodes(
          thumbnails,
          this.productMediaRoomSnapshot.zoomThumbnails,
          images,
          mediaIds,
          'zoomThumbnail'
        );
        const updatedZoomItems = this.replaceRoomGalleryNodes(
          mediaList,
          this.productMediaRoomSnapshot.zoomItems,
          images,
          mediaIds,
          'zoom'
        );

        this.productMediaRoomGalleryMutated = !!(
          updatedMain ||
          updatedThumbnails ||
          updatedGrid ||
          updatedZoomThumbnails ||
          updatedZoomItems
        );

        this.refreshZoomDialogCache();
        this.initializeRoomGallerySwipers();
        await this.waitForImages(this.getMediaElement(mediaIds[0]) || this);
        this.setActiveMedia(mediaIds[0], false);
        this.fadeOutVariantOverlay(overlay);

        return this.productMediaRoomGalleryMutated;
      }

      replaceRoomGalleryNodes(wrapper, templates, images, mediaIds, nodeType) {
        if (!wrapper || !templates?.length || !images.length) return false;

        const template = this.getRoomGalleryTemplate(templates);
        if (!template) return false;

        wrapper.innerHTML = '';
        images.forEach((image, index) => {
          wrapper.appendChild(this.createRoomGalleryNode(template, image, mediaIds[index], index, images.length, nodeType));
        });

        return true;
      }

      getRoomGalleryTemplate(templates) {
        return templates.find((node) => node.querySelector('img')) || templates[0] || null;
      }

      createRoomGalleryNode(template, image, mediaId, index, total, nodeType) {
        const node = template.cloneNode(true);

        this.resetRoomGalleryNodeState(node);
        this.setRoomGalleryNodeIndexes(node, index, total, nodeType);
        this.setRoomGalleryNodeMediaId(node, mediaId);
        this.applyRoomImageToNode(node, image, nodeType);

        return node;
      }

      resetRoomGalleryNodeState(node) {
        node.classList.remove(
          'active',
          'hidden',
          'swiper-slide-active',
          'swiper-slide-next',
          'swiper-slide-prev',
          'swiper-slide-visible',
          'swiper-slide-thumb-active'
        );
        node.removeAttribute('data-swiper-slide-index');
        node.style.removeProperty('width');
        node.style.removeProperty('margin-right');
      }

      setRoomGalleryNodeIndexes(node, index, total, nodeType) {
        if (index > 0) {
          node.querySelectorAll('.card__badge, .video-dialog').forEach((element) => element.remove());
        }

        if (nodeType === 'thumbnail' || node.classList.contains('swiper-controls__thumbnail')) {
          node.dataset.index = String(index);
          node.setAttribute('aria-label', `Slide ${index + 1} of ${total}`);
        }

        if (nodeType === 'zoomThumbnail' || node.classList.contains('dialog-thumbnails-list__thumbnail')) {
          node.dataset.zoomIndex = String(index);
          node.setAttribute('aria-selected', String(index === 0));
          node.setAttribute('aria-label', `Slide ${index + 1} of ${total}`);
        }

        node.querySelectorAll('[data-zoom-index]').forEach((trigger) => {
          trigger.dataset.zoomIndex = String(index);
        });

        if (node.hasAttribute('data-zoom-index')) {
          node.dataset.zoomIndex = String(index);
        }

        const slideshowSlide = node.querySelector('.slideshow__slide');
        if (slideshowSlide) {
          slideshowSlide.setAttribute('aria-hidden', String(index !== 0));
        }
      }

      setRoomGalleryNodeMediaId(node, mediaId) {
        if (!mediaId) return;

        if (
          node.classList.contains('product-media-container') ||
          node.classList.contains('media-gallery__grid-item') ||
          node.classList.contains('swiper-slide')
        ) {
          node.dataset.mediaId = mediaId;
        }

        const mediaIdNodes = [];
        if (node.hasAttribute('data-media-id')) mediaIdNodes.push(node);
        mediaIdNodes.push(...node.querySelectorAll('[data-media-id]'));

        mediaIdNodes.forEach((element) => {
          element.dataset.mediaId = mediaId;
          element.setAttribute('data-media-id', mediaId);
        });
      }

      applyRoomImageToNode(node, imageData, nodeType) {
        const image = this.normalizeImageData(imageData);
        if (!image) return;

        const useThumbnailSource = nodeType === 'thumbnail' || nodeType === 'zoomThumbnail';
        node.querySelectorAll('img').forEach((img) => {
          this.applyRoomImageToElement(img, image, useThumbnailSource);
        });

        this.applyRoomImageRatio(node, image);
      }

      applyRoomImageToElement(img, image, useThumbnailSource = false) {
        const src = useThumbnailSource ? image.thumbSrc || image.src : image.src;
        if (!src) return;

        if (useThumbnailSource) {
          img.removeAttribute('srcset');
          img.removeAttribute('sizes');
        } else if (image.srcset) {
          img.srcset = image.srcset;
        } else {
          img.removeAttribute('srcset');
        }

        img.src = src;
        if (image.alt) img.alt = image.alt;

        if (image.width) img.width = image.width;
        if (image.height) img.height = image.height;

        const zoomSource = image.zoomSrc || image.src;
        if (zoomSource) {
          img.setAttribute('data_max_resolution', zoomSource);
          img.setAttribute('data-max-resolution', zoomSource);
        }
      }

      applyRoomImageRatio(node, image) {
        const aspectRatio =
          Number(image.aspectRatio) ||
          (Number(image.width) && Number(image.height) ? Number(image.width) / Number(image.height) : 1);

        if (!aspectRatio) return;

        const ratioTargets = [node, ...node.querySelectorAll('.product-media-container, .product-media, [style*="--preview-ratio"], [style*="--media-preview-ratio"]')];
        ratioTargets.forEach((target) => {
          target.style.setProperty('--ratio', aspectRatio);
          target.style.setProperty('--preview-ratio', aspectRatio);
          target.style.setProperty('--media-preview-ratio', aspectRatio);
        });
      }

      destroyRoomGallerySwipers() {
        try {
          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;

          const mainSwiper = swiperComponent.initSwiper || this.querySelector('.swiper-main')?.swiper;
          if (mainSwiper && typeof mainSwiper.destroy === 'function') {
            mainSwiper.destroy(true, true);
          }

          const thumbsContainer = this.querySelector('.swiper-controls__thumbnails-container .swiper');
          const thumbsSwiper = thumbsContainer?.swiper || swiperComponent._thumbsSwiper;
          if (thumbsSwiper && typeof thumbsSwiper.destroy === 'function') {
            thumbsSwiper.destroy(true, true);
          }

          swiperComponent.initSwiper = null;
          swiperComponent._thumbsSwiper = null;
          swiperComponent._items = null;

          if (swiperComponent.swiperEl) {
            swiperComponent.swiperEl._swiperInitialized = false;
          }

          if (thumbsContainer) {
            thumbsContainer._swiperInitialized = false;
          }
        } catch (e) {
          // silent
        }
      }

      initializeRoomGallerySwipers() {
        try {
          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent || typeof swiperComponent.initializeSwiper !== 'function') return;

          swiperComponent._items = null;
          if (swiperComponent.swiperEl) {
            swiperComponent.swiperEl._swiperInitialized = false;
          }

          const thumbsContainer = this.querySelector('.swiper-controls__thumbnails-container .swiper');
          if (thumbsContainer) {
            thumbsContainer._swiperInitialized = false;
          }

          swiperComponent.initializeSwiper();
        } catch (e) {
          // silent
        }
      }

      refreshZoomDialogCache() {
        const { zoomDialog, thumbnails } = this.getZoomDialogParts();
        if (!zoomDialog) return;

        zoomDialog.media = Array.from(zoomDialog.querySelectorAll('.dialog-zoomed-gallery .product-media-container'));
        zoomDialog.thumbnails = thumbnails;
        zoomDialog.highResImagesLoaded = new Set();
      }

      /**
       * Sync slideshow/swiper component when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       * @param {boolean} smooth - Whether to use smooth transition
       */
      syncSlideshow(mediaElement, mediaIndex, smooth = false) {
        try {
          if (!this.classList.contains('media-gallery--carousel')) return;

          const swiperComponent = this.querySelector('swiper-component');
          if (!swiperComponent) return;
          
          const waitForSwiper = (retries = 10) => {
            if (swiperComponent.initSwiper) {
              const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
              let slideIndex = mediaIndex;
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              }

              if (slideIndex >= 0 && slideIndex < slides.length && swiperComponent.initSwiper) {
                swiperComponent.initSwiper.slideTo(slideIndex, smooth ? 800 : 0);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForSwiper(retries - 1), 50);
            }
          };

          waitForSwiper();
        } catch (e) {
          try {
            if (this.slideshow && typeof this.slideshow.goTo === 'function') {
              this.slideshow.goTo(mediaIndex);
            }
          } catch (err) {
            // silent
          }
        }
      }

      /**
       * Sync thumbnails controls when media is activated
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      syncThumbnails(mediaElement, mediaIndex) {
        try {
          const thumbnailsContainer = this.querySelector('.swiper-controls__thumbnails-container');
          if (!thumbnailsContainer) return;

          const thumbnailButtons = Array.from(thumbnailsContainer.querySelectorAll('.swiper-controls__thumbnail[data-index]'));
          if (thumbnailButtons.length === 0) return;

          let slideIndex = mediaIndex;
          
          if (this.classList.contains('media-gallery--carousel')) {
            const swiperComponent = this.querySelector('swiper-component');
            if (swiperComponent) {
              const slideElement = mediaElement.closest('.swiper-slide');
              if (slideElement) {
                const slides = Array.from(swiperComponent.querySelectorAll('.swiper-wrapper .swiper-slide'));
                const foundIndex = slides.indexOf(slideElement);
                if (foundIndex >= 0) {
                  slideIndex = foundIndex;
                }
              } else {
                if (swiperComponent.initSwiper && typeof swiperComponent.initSwiper.activeIndex === 'number') {
                  slideIndex = mediaIndex;
                }
              }
            }
          }

          let targetThumbnail = thumbnailButtons.find((button) => {
            const index = parseInt(button.getAttribute('data-index'), 10);
            return !isNaN(index) && index === slideIndex;
          });

          if (!targetThumbnail) {
            targetThumbnail = thumbnailButtons.find((button) => {
              const index = parseInt(button.getAttribute('data-index'), 10);
              return !isNaN(index) && index === mediaIndex;
            });
          }

          if (targetThumbnail) {
            const finalSlideIndex = parseInt(targetThumbnail.getAttribute('data-index'), 10);

            this.syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, finalSlideIndex);
          }
        } catch (e) {
          // silent
        }
      }

      /**
       * Sync thumbnail swiper to show the active thumbnail
       * @param {HTMLElement} thumbnailsContainer - The thumbnail container element
       * @param {Array} thumbnailButtons - Array of all thumbnail buttons
       * @param {HTMLElement} targetThumbnail - The active thumbnail button
       * @param {number} slideIndex - The slide index to sync to
       */
      syncThumbnailSwiper(thumbnailsContainer, thumbnailButtons, targetThumbnail, slideIndex) {
        try {
          const waitForThumbnailSwiper = (retries = 10) => {
            const swiperComponent = this.querySelector('swiper-component');
            let thumbsSwiper = null;

            if (swiperComponent && swiperComponent.initSwiper && swiperComponent.initSwiper.params && swiperComponent.initSwiper.params.thumbs) {
              thumbsSwiper = swiperComponent.initSwiper.params.thumbs.swiper;
            }

            if (!thumbsSwiper) {
              const thumbnailSwiper = thumbnailsContainer.querySelector('.swiper');
              if (thumbnailSwiper && thumbnailSwiper.swiper) {
                thumbsSwiper = thumbnailSwiper.swiper;
              }
            }

            if (thumbsSwiper) {
              if (slideIndex >= 0 && slideIndex < thumbnailButtons.length) {
                thumbsSwiper.slideTo(slideIndex, 300);
              }
            } else if (retries > 0) {
              setTimeout(() => waitForThumbnailSwiper(retries - 1), 50);
            }
          };

          waitForThumbnailSwiper();
        } catch (e) {
          // silent
        }
      }

      /**
       * Swap media in grid layout: move variant image to first position
       * @param {HTMLElement} mediaElement - The active media element
       * @param {number} mediaIndex - The index of the media in the gallery
       */
      swapGridMedia() {
        try {
          if (typeof MainEvents !== 'undefined' && MainEvents.variantUpdate) {
            const handleVariantUpdateOnce = async (event) => {
              let overlay = null;
              try {
                const source = event?.detail?.data?.html;
                if (!source) return;

                const newMediaGallery = source.querySelector('media-gallery');
                if (!newMediaGallery) return;

                overlay = this.createVariantFadeOverlay();
                await this.waitForImages(newMediaGallery);
                this.replaceWith(newMediaGallery);
                this.fadeOutVariantOverlay(overlay);

                try {
                  newMediaGallery.scrollIntoView({ behavior: 'smooth' });
                } catch (e) {
                  // ignore scrolling errors
                }
              } catch (e) {
                this.fadeOutVariantOverlay(overlay);
                // silent
              } finally {
                document.removeEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
              }
            };
            document.addEventListener(MainEvents.variantUpdate, handleVariantUpdateOnce);
          }
          
        } catch (e) {
          // silent
        }
      }

      waitForImages(root) {
        if (!root) return Promise.resolve();

        const imageScope =
          root.querySelector('.active') ||
          root.querySelector('[data-media-id]') ||
          root;
        const images = Array.from(imageScope.querySelectorAll('img'));
        const pendingImages = images.filter((image) => !image.complete);

        if (!pendingImages.length) return Promise.resolve();

        pendingImages.forEach((image) => {
          image.loading = 'eager';
        });

        return Promise.race([
          Promise.all(
            pendingImages.map(
              (image) => this.preloadImage(image)
            )
          ),
          new Promise((resolve) => setTimeout(resolve, 6000)),
        ]);
      }

      preloadImage(image) {
        return new Promise((resolve) => {
          let complete = false;
          const finish = () => {
            if (complete) return;
            complete = true;
            resolve();
          };
          const finishAfterDecode = (target) => {
            if (target && typeof target.decode === 'function') {
              target.decode().then(finish).catch(finish);
            } else {
              finish();
            }
          };

          image.addEventListener('load', () => finishAfterDecode(image), { once: true });
          image.addEventListener('error', finish, { once: true });

          if (image.complete) {
            finishAfterDecode(image);
            return;
          }

          const preloader = new Image();
          preloader.decoding = 'async';
          if (image.sizes) preloader.sizes = image.sizes;
          if (image.srcset) preloader.srcset = image.srcset;
          preloader.addEventListener('load', () => finishAfterDecode(preloader), { once: true });
          preloader.addEventListener('error', finish, { once: true });
          const preloadSource = image.currentSrc || image.src;
          if (!preloadSource) {
            finish();
            return;
          }
          preloader.src = preloadSource;
        });
      }
    }
  );
}
